import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { collectAll } from '@/lib/collectors';
import { buildSnapshot } from '@/lib/scoring';
import { detectChanges } from '@/lib/changeDetection';
import { generateBrief } from '@/lib/brief';
import {
  appendObservations,
  appendHistoryPoint,
  getHistory,
  getHistorySpanMs,
  scoreTrend,
  recordPhase,
  seedFromServer,
} from '@/lib/store';
import type { ScoreSnapshot, ChangeEvent, HistoryPoint } from '@/lib/types';
import type { Brief } from '@/lib/brief';
import { useScoringWeights } from './useScoringWeights';

const DAY = 24 * 60 * 60 * 1000;

export interface LiquidityData {
  snapshot: ScoreSnapshot;
  changes: ChangeEvent[];
  brief: Brief;
  /** Score history series (oldest first), for sparklines / trend charts. */
  history: HistoryPoint[];
  trend30d: { liquidity: number; bch: number };
  /** True once enough real history exists to trust trend-driven readings. */
  trendReliable: boolean;
  liveCount: number;
  /** indicators with a usable value (fresh or delayed), i.e. not unavailable */
  availableCount: number;
  totalIndicators: number;
  lastUpdated: number;
  serverSourced: boolean;
}

/**
 * The single orchestration hook. Runs the collect -> store -> score ->
 * detect -> brief pipeline and caches the result. Refetches every 5 minutes.
 */
export function useLiquidityData() {
  const { weights } = useScoringWeights();
  const weightsKey = useMemo(() => JSON.stringify(weights), [weights]);

  return useQuery<LiquidityData>({
    queryKey: ['liquidity-data', weightsKey],
    refetchInterval: 5 * 60 * 1000,
    staleTime: 4 * 60 * 1000,
    queryFn: async ({ signal }) => {
      // Seed local history from the server's 24/7 series (once per 6h) so
      // change detection reflects data collected while the app was closed.
      await seedFromServer(signal);

      const { observations, liveCount, serverSourced } = await collectAll(signal);

      // Persist BEFORE reading trends so the first run seeds history.
      appendObservations(observations);

      const trend30d = {
        liquidity: scoreTrend('liquidity', 30 * DAY),
        bch: scoreTrend('bch', 30 * DAY),
      };

      // Only trust a trend-driven cycle phase once we hold enough genuine
      // history. Below this, a fresh data-source migration could otherwise
      // read as a real 30-day "easing" move. Fall back to a level-only phase.
      const trendReliable = getHistorySpanMs() >= 21 * DAY;

      const snapshot = buildSnapshot(observations, trend30d, undefined, trendReliable);

      // Record this score point + phase to history.
      appendHistoryPoint({
        t: snapshot.timestamp,
        liquidity: snapshot.liquidity.score,
        bch: snapshot.bch.score,
        phase: snapshot.phase,
      });
      recordPhase(snapshot.phase);

      const changes = detectChanges(observations);
      const brief = generateBrief(snapshot, changes, trend30d);
      const history = getHistory();

      const availableCount = observations.filter(
        (o) => o.quality !== 'unavailable' && o.value !== null,
      ).length;

      return {
        snapshot,
        changes,
        brief,
        history,
        trend30d,
        trendReliable,
        liveCount,
        availableCount,
        totalIndicators: observations.length,
        lastUpdated: snapshot.timestamp,
        serverSourced,
      };
    },
  });
}
